import {Component, ElementRef, NgZone, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {
  fadeInLeftOnEnterAnimation,
  fadeInOnEnterAnimation,
  fadeOutOnLeaveAnimation,
  fadeOutRightOnLeaveAnimation
} from 'angular-animations';
import {AnalyticsService} from '../../../services/analytics.service';
import {Subscription} from 'rxjs';
import {UserService} from '../../../services/user.service';
import {ActivatedRoute} from '@angular/router';
import {NavService} from '../../../services/nav.service';
import {JourneyService} from '../../journey/service/journey.service';
import {TasksModel} from '../../tasks/models/tasks.model';
import {NapiService} from '../../../services/napi.service';
import {ZoomCredentials} from '../models/zoom.model';
import {ConferenceService} from '../service/conference.service';
import {environment} from '../../../../environments/environment';
import {EmojiDto} from '../models/card.model';

interface PageLayoutDto {
  isZoomEnabled: boolean;
  pageLayout: string; // 'dockLeft', 'dockBottom'
}

interface ChangeIsResultsDto {
  isResults: boolean;
}

@Component({
  selector: 'app-conference-page',
  templateUrl: './conference.page.html',
  styleUrls: ['./conference.page.scss'],
  animations: [
    fadeInOnEnterAnimation({anchor: 'enter', duration: 500}),
    fadeOutOnLeaveAnimation({anchor: 'leave', duration: 500}),
    fadeOutRightOnLeaveAnimation({anchor: 'leaveList', duration: 300}),
    fadeInLeftOnEnterAnimation({anchor: 'enterList', duration: 300}),
  ]
})

export class ConferencePageComponent implements OnInit, OnDestroy {
  subscriptions: Subscription[] = [];

  task: TasksModel = null;

  interactionId = null;
  interaction = null;

  urlSocial = null;

  isSocialActive = false;
  isInteractionActive = false;
  isZoomActive: boolean = false;
  isResultsActive: boolean = false;
  isTalkJSActive: boolean = false;
  isWordCloudReady: boolean = false;

  isZoomEnabled: boolean = false;
  zoomCredentials: ZoomCredentials = null;

  speakerVideoType: string = 'speaker'; // speaker, ribbon

  isInPerson: boolean = true

  pageLayout: string = 'dockBottom'; // dockBottom, dockLeft
  userName = 'Anonymous User';
  userEmail = 'anonymous@user.com';

  socket = null;

  emoji: EmojiDto = null;

  @ViewChild('speakerVideo', {static: false}) speakerVideoRef!: ElementRef;
  @ViewChild('main', {static: false}) mainRef!: ElementRef;

  constructor(
    private ngZone: NgZone,
    private analytics: AnalyticsService,
    private activatedRoute: ActivatedRoute,
    private nav: NavService,
    public userService: UserService,
    private napiService: NapiService,
    private journeyService: JourneyService,
    private conferenceService: ConferenceService,
  ) {
    this.userName = this.userService.userInfo.full_name;
    this.userEmail = this.userService.userInfo.email;
    this.isInPerson = this.userService.userInfo.is_inperson;

    this.urlSocial = 'https://app.meet.ps/attendee/28bkkso8' + '?i=' + this.userService.userInfo.meet_user;
    this.isSocialActive = (!this.isInPerson && environment.production);

    if (this.userService.userInfo.client_id === 131) {
      this.isTalkJSActive = true;
      this.urlSocial = 'https://app.meet.ps/attendee/28bkkso8' + '?i=' + this.userService.userInfo.meet_user;
    } else if (this.userService.userInfo.client_id === 197) {
      this.urlSocial = 'https://app.meet.ps/attendee/j4xoi604' + '?i=' + this.userService.userInfo.meet_user;
      this.speakerVideoType = 'ribbon';
    }

    const task_id = parseInt(this.activatedRoute.snapshot.paramMap.get('task_id'), 10) || 0;
    if (!task_id) {
      this.nav.navigateHome();
    } else {
      this.journeyService.getMyTask(task_id).then(task => {
        this.task = task;
        if (!task) {
          this.nav.navigateHome();
        } else {
          this.zoomCredentials = {
            sdkKey: environment.zoomSdkKey,
            meetingNumber: this.task.url,
            passcode: this.task.field1,
            userName: this.userName,
            userEmail: this.userEmail
          };

          // If interaction ID is provided, then initialize WebSocket
          if (this.task.field2) {
            this.initInteraction(this.task.field2);
            this.initWebSocket(this.task.field2);
          } else {
            if (!this.isInPerson) {
              this.isZoomEnabled = true;
            }
          }
        }
      });
    }
  }

  ngOnInit() {
  }

  ionViewDidEnter() {
    this.analytics.trackView('Conference');
  }

  protected initInteraction(interactionId: string) {
    this.conferenceService.getActiveCards(interactionId).then(interaction => {
      if (interaction) {
        this.interactionId = interactionId;
        this.interaction = interaction;

        if (interaction.interactionsGroups
          && interaction.interactionsGroups.length > 0
          && interaction.interactionsGroups[0].interactionsCards
          && interaction.interactionsGroups[0].interactionsCards.length > 0
        ) {
          this.isInteractionActive = true;
          this.isResultsActive = interaction.interactionsGroups[0].isResults;
          this.isWordCloudReady = this.isResultsActive;

          console.log('this.isResultsActive', this.isResultsActive);
        } else {
          this.isInteractionActive = false;
          this.isResultsActive = false;
          this.isWordCloudReady = false;
        }

        if (!this.isInPerson) {
          this.pageLayout = interaction.pageLayout;
          if (interaction.isZoomEnabled) {
            this.activateZoom();
          }
        }
      } else {
        this.clearInteraction();
      }
    }).catch(() => {
      this.clearInteraction();
    });
  }

  protected clearInteraction() {
    this.interactionId = null;
    this.interaction = null;
    this.isInteractionActive = false;
    this.isResultsActive = false;
    this.isWordCloudReady = false;
  }

  protected initWebSocket(interactionId: string) {
    this.socket = this.napiService.socket('interactions');

    this.socket.on('connect', () => {
      console.log('Connected to WebSocket server');
      this.socket.emit('joinRoom', interactionId); // Join to room 'Interaction ID'
    });

    this.socket.on('disconnect', function () {
      console.log('Disconnect client event...');
    });

    this.socket.on('message', data => {
      this.handleInteractionMessage(data);
    });
  }

  protected handleInteractionMessage(payload) {
    console.log('Received message:', payload); // 'setActive', 'setInactive'
    this.ngZone.run(() => {
      switch (payload.message) {
        case 'setActive':
          this.initInteraction(payload.interactionId);
          break;
        case 'setInactive':
          this.initInteraction(payload.interactionId);
          break;
        case 'updateControlLayout':
          this.setPageLayout(payload.data);
          break;
        case 'WordCloudStart':
          this.isWordCloudReady = false;
          break;
        case 'WordCloudComplete':
          this.isWordCloudReady = true;
          break;
        case 'changeIsResults':
          this.changeIsResult(payload.data);
          break;
        case 'emoji':
          this.onEmojiReceive(payload.data.emoji);
          break;
      }
    });
  }

  protected setPageLayout(layout: PageLayoutDto) {
    if (layout.isZoomEnabled) {
      this.activateZoom();
    } else {
      this.deactivateZoom();
    }
    this.pageLayout = layout.pageLayout;
  }

  protected changeIsResult(payload: ChangeIsResultsDto) {
    this.isWordCloudReady = false;
    this.isResultsActive = payload.isResults;
  }

  protected activateZoom() {
    if (!this.isInPerson) {
      this.isZoomActive = true;
      this.isZoomEnabled = true;
    }
  }

  protected deactivateZoom() {
    // this.isZoomActive = false;
    this.isZoomEnabled = false;
  }

  protected onEmojiSend(name: string) {
    this.socket.emit('sendEmoji', {room: this.interactionId, emoji: {
        name,
        userId: this.userService.userInfo.id,
        userFullName: this.userService.userInfo.full_name,
      }});
  }

  protected onEmojiReceive(emoji: EmojiDto) {
    if (emoji !== null && emoji !== undefined && emoji.userId !== this.userService.userInfo.id) {
      this.emoji = emoji;
    }
  }

  ngOnDestroy(): void {
    if (this.socket) {
      this.socket.disconnect();
    }

    this.subscriptions.forEach(i => i.unsubscribe());
  }
}
